import type { Observation } from "../../lib/observations";

/**
 * The recharts-free half of the history charts: the shapes the API returns,
 * the range picker's options, and the pure helpers over them.
 *
 * It lives apart from PriceTrendChart so ProductPage can read `RANGES` and
 * `totalSamplesFor` without importing anything that reaches recharts —
 * that's what lets the charts load as their own chunk (see HistoryCharts).
 */

export type HistoryPoint = {
	fetchedAt: number;
	priceMinor: number | null;
	inStock: boolean;
	stockLevel: number | null;
};

export type VariantHistory = {
	productId: number;
	storeId: string;
	observations: Observation[];
};

export type GroupHistory = {
	rangeMs: number;
	sinceMs: number;
	variants: VariantHistory[];
};

export type Range = "24h" | "7d" | "30d";

export const RANGES: Array<{ key: Range; label: string }> = [
	{ key: "24h", label: "24h" },
	{ key: "7d", label: "7d" },
	{ key: "30d", label: "30d" },
];

// Each observation is a flat segment: the state held from `from` to `to`.
// Expand it into its edge points so downstream consumers (built around
// point-in-time snapshots) keep working, while the actual chart is drawn
// with stepAfter so the flat segments render truthfully instead of as an
// interpolated curve between values that never existed in between.
export function expandObservations(observations: Observation[]): HistoryPoint[] {
	return observations.flatMap((o) => {
		const point = {
			priceMinor: o.priceMinor,
			inStock: o.inStock,
			stockLevel: o.stockLevel,
		};
		return o.to > o.from
			? [
					{ fetchedAt: o.from, ...point },
					{ fetchedAt: o.to, ...point },
				]
			: [{ fetchedAt: o.from, ...point }];
	});
}

export function totalSamplesFor(history: GroupHistory): number {
	return history.variants.reduce((acc, v) => acc + v.observations.reduce((n, o) => n + o.samples, 0), 0);
}

export function formatTick(ts: number, range: Range): string {
	const d = new Date(ts);
	if (range === "24h") {
		return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
	}
	return d.toLocaleDateString([], { month: "short", day: "2-digit" });
}
